"use client";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@ethera/ui/accordion";
import { cn } from "@guru/shared/utils/cn";
import { HelpCircle, type LucideIcon } from "lucide-react";
import posthog from "posthog-js";

export interface FaqItem {
  question: string;
  answer: string;
}

interface FaqListProps {
  items: FaqItem[];
  /** Decorative icon per item, aligned by index. Falls back to a question mark. */
  icons?: LucideIcon[];
  /** PostHog event fired when an item is opened. */
  eventName: string;
  /** Keeps accordion item values unique when several lists share a page. */
  idPrefix?: string;
  className?: string;
}

/**
 * Enriched FAQ accordion shared by the homepage and pricing surfaces. Each row
 * is a pointer-aware card: a tinted icon tile, a saffron rail that fills as the
 * answer opens, and the answer indented to line up beneath the question. The
 * accordion itself stays a real Radix accordion so keyboard and screen-reader
 * behaviour is untouched; everything extra is presentation.
 */
export function FaqList({ items, icons, eventName, idPrefix = "faq", className }: FaqListProps) {
  return (
    <Accordion type="single" collapsible className={cn("w-full space-y-3", className)}>
      {items.map((item, index) => {
        const Icon = icons?.[index] ?? HelpCircle;
        return (
          <AccordionItem
            // biome-ignore lint/suspicious/noArrayIndexKey: Static, ordered translation array
            key={index}
            value={`${idPrefix}-${index}`}
            className={cn(
              "group/faq relative overflow-hidden rounded-xl border border-border bg-card",
              "spotlight-card transition-all duration-300",
              "hover:border-brand/40 hover:shadow-sm",
              "has-[[data-state=open]]:border-brand/50 has-[[data-state=open]]:bg-brand/[0.03] has-[[data-state=open]]:shadow-md"
            )}
            onPointerMove={(event) => {
              const el = event.currentTarget;
              const rect = el.getBoundingClientRect();
              el.style.setProperty("--spot-x", `${event.clientX - rect.left}px`);
              el.style.setProperty("--spot-y", `${event.clientY - rect.top}px`);
            }}
          >
            {/* Saffron rail that fills downward as the answer opens */}
            <span
              aria-hidden="true"
              className={cn(
                "absolute inset-y-0 left-0 w-[3px] origin-top scale-y-0 bg-brand",
                "transition-transform duration-300 ease-out",
                "group-has-[[data-state=open]]/faq:scale-y-100"
              )}
            />
            <AccordionTrigger
              onClick={() => posthog.capture(eventName, { question: item.question })}
              className="group/trigger items-center gap-3 px-4 py-4 text-left text-base hover:no-underline sm:px-5"
            >
              <span className="flex items-center gap-3">
                <span
                  className={cn(
                    "flex size-9 shrink-0 items-center justify-center rounded-lg",
                    "bg-brand/10 text-brand-deep ring-1 ring-brand/15",
                    "transition-colors duration-300",
                    "group-data-[state=open]/trigger:bg-brand group-data-[state=open]/trigger:text-brand-contrast group-data-[state=open]/trigger:ring-brand"
                  )}
                >
                  <Icon className="size-[18px]" aria-hidden="true" />
                </span>
                <span className="font-medium text-foreground">{item.question}</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-4 sm:px-5">
              <div className="flex gap-3">
                {/* Spacer aligns the answer with the question text on wider screens */}
                <span className="hidden w-9 shrink-0 sm:block" aria-hidden="true" />
                <p className="text-pretty leading-relaxed text-muted-foreground">{item.answer}</p>
              </div>
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}
